// Feature Requests System - TypeScript Types
// Phase 3 Roadmap Implementation

export interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category?: string;
  status: FeatureStatus;
  priority: number; // 0-10
  votes_count: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
  admin_notes?: string;
  target_version?: string;
  estimated_days?: number;
  github_issue_url?: string;
  deleted_at?: string | null;
  title_cache?: string; // Denormalized cache for search fallback
  description_cache?: string;
  default_language?: string; // Language of original request
}

export interface FeatureVote {
  id: string;
  feature_request_id: string;
  user_id: string;
  created_at: string;
}

export interface FeatureComment {
  id: string;
  feature_request_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  /** Populé côté client via join sur profiles (pas en DB) */
  author_name?: string;
  author_email?: string;
}

export interface CreateFeatureCommentDto {
  feature_request_id: string;
  content: string;
}

export type FeatureStatus =
  | "proposed"
  | "under_review"
  | "planned"
  | "in_progress"
  | "completed"
  | "rejected";

export type FeatureCategory =
  | "export"
  | "integration"
  | "ui"
  | "mobile"
  | "performance"
  | "security"
  | "analytics"
  | "collaboration"
  | "other";

// DTOs for API operations
export interface CreateFeatureRequestDto {
  title: string;
  description: string;
  category?: FeatureCategory;
  screenshot_url?: string;
  receive_updates?: boolean;
}

export interface UpdateFeatureRequestDto {
  title?: string;
  description?: string;
  category?: FeatureCategory;
  status?: FeatureStatus;
  priority?: number;
  admin_notes?: string;
  target_version?: string;
  estimated_days?: number;
  github_issue_url?: string;
}

export interface FeatureRequestFilters {
  status?: FeatureStatus;
  category?: FeatureCategory;
  search?: string;
  sort_by?: "created_at" | "votes_count" | "priority" | "updated_at";
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  language?: string; // ISO 639-1: 'fr', 'en', 'es', etc.
}

// Duplicate detection
export interface DuplicateMatch {
  feature: FeatureRequest;
  similarity: number;
  reasons: string[];
}

export interface DuplicateDetectionResult {
  hasDuplicates: boolean;
  matches: DuplicateMatch[];
  confidence: number;
}

// Analytics
export interface FeatureRequestAnalytics {
  total_requests: number;
  by_status: Record<FeatureStatus, number>;
  by_category: Record<FeatureCategory, number>;
  average_votes_per_request: number;
  top_voted: FeatureRequest[];
  recent_activity: FeatureRequest[];
  completion_rate: number;
  average_time_to_completion: number; // in days
  total_votes?: number;
  active_users?: number;
}

// Admin operations
export interface BulkAction {
  action: "approve" | "reject" | "delete" | "merge";
  feature_ids: string[];
  target_status?: FeatureStatus;
  merge_target_id?: string;
  reason?: string;
}

export interface FeatureRequestStats {
  total: number;
  proposed: number;
  under_review: number;
  planned: number;
  in_progress: number;
  completed: number;
  rejected: number;
  total_votes: number;
  average_votes: number;
}

// Notification types
export interface FeatureRequestNotification {
  type: "created" | "status_changed" | "comment_added" | "vote_received";
  feature_request: FeatureRequest;
  user_id?: string;
  message: string;
  created_at: string;
}

// Response types
export interface FeatureRequestServiceResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string | FeatureRequestError;
  message?: string;
}

export interface FeatureRequestWithVote extends FeatureRequest {
  user_vote?: FeatureVote;
  vote_count?: number;
}

export interface VoteValidation {
  valid: boolean;
  error?: string;
  existing_vote?: FeatureVote;
}

// Error types
export class FeatureRequestError extends Error {
  code: string;
  details?: any;

  constructor(message: string, code: string, details?: any) {
    super(message);
    this.name = "FeatureRequestError";
    this.code = code;
    this.details = details;
  }
}

// Vote validation
export interface VoteValidation {
  valid: boolean;
  error?: string;
  existing_vote?: FeatureVote;
}

// Feature request with user vote info
export interface FeatureRequestWithVote extends FeatureRequest {
  user_has_voted?: boolean;
  user_vote?: FeatureVote;
}

// Export types
export interface FeatureRequestExport {
  format: "csv" | "json" | "pdf";
  filters?: FeatureRequestFilters;
  include_analytics?: boolean;
}
